import { makeAutoObservable, runInAction } from "mobx";
import type { MutableRefObject } from "react";
import { v4 as uuidv4 } from "uuid";
import { APPS } from "../../../../../constants/AppsConstants";

import { BrushActions } from "../../../../../hooks/brush/brushActions.ts";
import { BrushCanvasRefs, IBrushSettings } from "../../../../../models/brush.ts";
import type { ImageToImageConfigType } from "../../../../../models/image-to-image";
import type { AutomotiveReflectionLayer, PlaygroundImage, PlaygroundResult } from "../../../../../models/playground";
import QueryService from "../../../../../utils/QueryService";
import { DEFAULT_BRUSH } from "../../../ImageToImage/Config/Features/EraseObject/eraseObjectConstants.ts";
import type { IAutomotiveStore } from "../../automotive-store";

export interface IAutomotiveReflectionStore {
	config: AutomotiveReflectionConfig;

	loadingSaveReflection: boolean;

	handleFormChange: <K extends keyof AutomotiveReflectionConfig>(
		key: K,
		value: AutomotiveReflectionConfig[K],
	) => void;
	saveReflection: (layers: AutomotiveReflectionLayer[] | undefined, isTires?: boolean) => Promise<string | undefined>;
	segmentationRefs: BrushCanvasRefs[];
	selectedSegmentationRef?: MutableRefObject<Map<string, HTMLCanvasElement>> | undefined;
	brushConfigs: IBrushSettings;
	mergeSegmentationWithDrawing: (imageDimensions: { width: number; height: number }) => Promise<File | null>;
	handleResetBrusher: () => void;
	reflectionZoom: number;
	setReflectionZoom: (value: number) => void;
	magnifierEnabled: boolean;
	magnifierZoom: number;
	magnifierSize: number;
	setMagnifierEnabled: (enabled: boolean) => void;
	setMagnifierZoom: (zoom: number) => void;
	setMagnifierSize: (size: number) => void;
}

export class AutomotiveReflectionStore implements IAutomotiveReflectionStore {
	private queryService: QueryService = new QueryService("/automotive/reflection");
	automotiveStore: IAutomotiveStore;

	config: AutomotiveReflectionConfig = { preset: "" as AutomotivePreset };

	loadingSaveReflection = false;
	segmentationRefs: BrushCanvasRefs[] = [];
	selectedSegmentationRef: MutableRefObject<Map<string, HTMLCanvasElement>> | undefined;
	brushConfigs: IBrushSettings = {
		globalCompositeOperation: DEFAULT_BRUSH.globalCompositeOperation,
		strokeStyle: DEFAULT_BRUSH.strokeStyle,
		lineWidth: DEFAULT_BRUSH.lineWidth,
		brushColorOpacity: DEFAULT_BRUSH.brushColorOpacity,
		lineJoin: DEFAULT_BRUSH.lineJoin,
		lineCap: DEFAULT_BRUSH.lineCap,
		globalAlpha: DEFAULT_BRUSH.globalAlpha,
		reset: DEFAULT_BRUSH.reset,
		isMaskApplied: false,
		brushMode: DEFAULT_BRUSH.brushMode,
	};
	reflectionZoom: number = 1;
	magnifierEnabled = false;
	magnifierZoom = 3;
	magnifierSize = 150;

	constructor(automotiveStore: IAutomotiveStore) {
		makeAutoObservable(this);
		this.automotiveStore = automotiveStore;
	}

	setMagnifierEnabled = (enabled: boolean) => {
		this.magnifierEnabled = enabled;
	};

	setMagnifierZoom = (zoom: number) => {
		this.magnifierZoom = zoom;
	};

	setMagnifierSize = (size: number) => {
		this.magnifierSize = size;
	};

	handleFormChange = <K extends keyof AutomotiveReflectionConfig>(key: K, value: AutomotiveReflectionConfig[K]) => {
		runInAction(() => {
			this.config = { ...this.config, [key]: value };
		});
	};

	saveReflection = async (layers: AutomotiveReflectionLayer[] | undefined, isTires: boolean = false) => {
		this.loadingSaveReflection = true;
		const effect = this.automotiveStore.effectsStore.config.effect;
		const preset = this.automotiveStore.harmonizationStore.config.preset;
		const playgroundStore = this.automotiveStore.playgroundStore;
		const originalImage = this.automotiveStore.playgroundStore.singleSelectedImage;
		const savedIndex = playgroundStore.playgroundResults.length;

		try {
			const resultsSkeletons: PlaygroundResult = {
				id: uuidv4(),
				config: playgroundStore.selectedSingleImage?.config,
				type: APPS.AUTOMOTIVE,
				images: [
					{
						id: "",
						url: "",
						loading: true,
						selected: true,
						playground_result_id: "",
					},
				],
			};
			playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];

			const formData = new FormData();
			const finalImage = await this.createFinalReflectionImage(originalImage?.url, layers);

			finalImage && formData.append("file", finalImage);

			const imageUrl: string = await this.queryService.post("/", formData, {
				"Content-Type": "multipart/form-data",
			});

			runInAction(() => {
				const { playgroundResults, projectsStore } = playgroundStore;
				const tempPlaygroundResult = { ...playgroundResults[savedIndex] };

				const newImage: PlaygroundImage = {
					...originalImage,
					...(originalImage?.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: imageUrl,
					loading: true,
					type: APPS.AUTOMOTIVE,
					config: {
						...tempPlaygroundResult.config,
						original_image: { ...originalImage },
						effect: effect,
						preset: preset,
						refine_tires: isTires,
					} as ImageToImageConfigType,
					image_config: {
						refine_tires: isTires,
						effect: effect,
						preset: preset,
					} as ImageToImageConfigType,
				};

				tempPlaygroundResult.images = [newImage];
				playgroundResults[savedIndex].images = [{ ...newImage, loading: true, url: "" }];
				projectsStore
					.createPlaygroundResult(tempPlaygroundResult)
					.then((response) => {
						const newImageUrl = (response as any).images[0].url;
						playgroundResults[savedIndex].images[playgroundResults[savedIndex].images.length - 1].url =
							newImageUrl;
						playgroundStore.handleSelectSingleImage({ ...newImage, url: newImageUrl });
					})
					.catch((error) => {
						console.error("Error creating playground result:", error);
					})
					.finally(() => {
						this.loadingSaveReflection = false;
					});
			});

			return imageUrl;
		} catch (err: any) {
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
				return Promise.reject(err);
			});
		} finally {
			runInAction(() => {
				this.loadingSaveReflection = false;
			});
		}
	};

	async createFinalReflectionImage(
		baseImageUrl: string | undefined,
		reflectionLayers: AutomotiveReflectionLayer[] | undefined,
	): Promise<File | null> {
		if (!baseImageUrl || (reflectionLayers && reflectionLayers.length === 0)) return null;

		const baseImg = new Image();
		baseImg.crossOrigin = "anonymous";
		baseImg.src = baseImageUrl;

		return new Promise<File | null>((resolve, reject) => {
			baseImg.onload = async () => {
				const canvas = document.createElement("canvas");
				const width = baseImg.width;
				const height = baseImg.height;
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext("2d");

				if (!ctx) return reject("Canvas context not available");

				ctx.drawImage(baseImg, 0, 0, width, height);

				if (reflectionLayers) {
					await Promise.all(
						reflectionLayers.map(
							(layer) =>
								new Promise<void>((layerResolve) => {
									if (layer.hidden) return layerResolve();

									const img = new Image();
									img.crossOrigin = "anonymous";
									img.src = layer.url as string;
									img.onload = () => {
										ctx.save();
										ctx.globalAlpha = layer.intensity;
										ctx.drawImage(img, 0, 0, width, height);
										ctx.restore();
										layerResolve();
									};
									img.onerror = () => layerResolve();
								}),
						),
					);
				}

				canvas.toBlob((blob) => {
					if (blob) {
						const file = new File([blob], "final_image.png", { type: "image/png" });
						resolve(file);
					} else {
						resolve(null);
					}
				}, "image/png");
			};

			baseImg.onerror = reject;
		});
	}

	async mergeSegmentationWithDrawing(imageDimensions: { width: number; height: number }): Promise<File | null> {
		if (!this.segmentationRefs.length) return null;
		const playgroundStore = this.automotiveStore.playgroundStore;

		return new Promise<File | null>((resolve, reject) => {
			const firstRef = this.segmentationRefs[0];
			const firstCanvas = firstRef.canvasRef.current;

			if (!firstCanvas) return reject("Canvas reference is null");

			const outputWidth = imageDimensions.width;
			const outputHeight = imageDimensions.height;

			const mergedCanvas = document.createElement("canvas");
			mergedCanvas.width = outputWidth;
			mergedCanvas.height = outputHeight;

			const ctx = mergedCanvas.getContext("2d");
			if (!ctx) return reject("Canvas context not available");

			// Fill the canvas with black
			ctx.fillStyle = "black";
			ctx.fillRect(0, 0, outputWidth, outputHeight);

			const tempCanvas = document.createElement("canvas");
			tempCanvas.width = outputWidth;
			tempCanvas.height = outputHeight;
			const tempCtx = tempCanvas.getContext("2d");
			if (!tempCtx) return reject("Temp canvas context not available");

			// Extract the base image from canvasRef
			this.segmentationRefs.forEach(({ canvasRef }) => {
				const canvas = canvasRef.current;
				if (canvas) {
					tempCtx.clearRect(0, 0, outputWidth, outputHeight);

					tempCtx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, outputWidth, outputHeight);

					const imageData = tempCtx.getImageData(0, 0, outputWidth, outputHeight);
					const data = imageData.data;

					for (let i = 0; i < data.length; i += 4) {
						if (data[i + 3] > 0) {
							data[i] = 0;
							data[i + 1] = 0;
							data[i + 2] = 0;
							data[i + 3] = 255;
						}
					}

					tempCtx.putImageData(imageData, 0, 0);
					ctx.drawImage(tempCanvas, 0, 0);
				}
			});

			// Process the overlay canvas (user drawings)
			this.segmentationRefs.forEach(({ canvasOverlayRef }) => {
				const overlayCanvas = canvasOverlayRef.current;
				if (overlayCanvas) {
					tempCtx.clearRect(0, 0, outputWidth, outputHeight);

					// Apply the same zoom approach as we do in the UI
					// Instead of scaling the source dimensions, we'll draw at normal size
					// and let the transformation handle the scaling
					tempCtx.save();

					// Calculate the center point for the transformation
					const centerX = outputWidth / 2;
					const centerY = outputHeight / 2;

					// Apply the zoom transformation around the center point
					tempCtx.translate(centerX, centerY);
					tempCtx.scale(this.reflectionZoom, this.reflectionZoom);
					tempCtx.translate(-centerX, -centerY);

					tempCtx.drawImage(
						overlayCanvas,
						0,
						0,
						overlayCanvas.width,
						overlayCanvas.height,
						0,
						0,
						outputWidth,
						outputHeight,
					);

					tempCtx.restore();

					const imageData = tempCtx.getImageData(0, 0, outputWidth, outputHeight);
					const data = imageData.data;

					for (let i = 0; i < data.length; i += 4) {
						if (data[i + 3] > 0) {
							data[i] = 255;
							data[i + 1] = 255;
							data[i + 2] = 255;
							data[i + 3] = 255;
						}
					}

					tempCtx.putImageData(imageData, 0, 0);

					ctx.globalCompositeOperation = "lighten";
					ctx.drawImage(tempCanvas, 0, 0);
					ctx.globalCompositeOperation = "source-over";
				}
			});

			// Process the selected segmentation canvas
			if (this.selectedSegmentationRef && playgroundStore?.selectedSegmentation?.name) {
				const segName = playgroundStore.selectedSegmentation.name;
				const segCanvas = this.selectedSegmentationRef.current.get(segName);

				if (segCanvas) {
					tempCtx.clearRect(0, 0, outputWidth, outputHeight);

					// Apply the same zoom approach as we do in the UI
					tempCtx.save();

					// Calculate the center point for the transformation
					const centerX = outputWidth / 2;
					const centerY = outputHeight / 2;

					// Apply the zoom transformation around the center point
					tempCtx.translate(centerX, centerY);
					tempCtx.scale(this.reflectionZoom, this.reflectionZoom);
					tempCtx.translate(-centerX, -centerY);

					tempCtx.drawImage(
						segCanvas,
						0,
						0,
						segCanvas.width,
						segCanvas.height,
						0,
						0,
						outputWidth,
						outputHeight,
					);

					tempCtx.restore();

					const imageData = tempCtx.getImageData(0, 0, outputWidth, outputHeight);
					const data = imageData.data;

					for (let i = 0; i < data.length; i += 4) {
						if (data[i + 3] > 0) {
							if (data[i] > 100 && data[i + 2] > 150) {
								data[i] = 255;
								data[i + 1] = 255;
								data[i + 2] = 255;
								data[i + 3] = 255;
							}
						}
					}

					tempCtx.putImageData(imageData, 0, 0);

					ctx.globalCompositeOperation = "lighten";
					ctx.drawImage(tempCanvas, 0, 0);
					ctx.globalCompositeOperation = "source-over";
				}
			}

			// Convert to File
			mergedCanvas.toBlob((blob) => {
				if (blob) {
					resolve(new File([blob], "merged_segmentation.png", { type: "image/png" }));
				} else {
					resolve(null);
				}
			}, "image/png");
		});
	}

	handleResetBrusher = () => {
		if (this.segmentationRefs.length > 0) {
			const canvasRef = this.segmentationRefs[0]?.canvasRef.current;
			const overlayCanvasRef = this.segmentationRefs[0]?.canvasOverlayRef.current;

			if (overlayCanvasRef) {
				const brushActions = new BrushActions(canvasRef, overlayCanvasRef);

				// Clear the canvas
				brushActions.handleCanvasClear();

				// Update the reset flag in the store
				runInAction(() => {
					this.brushConfigs.reset = false;
				});
			}
		}
	};

	setReflectionZoom = (value: number) => {
		runInAction(() => {
			this.reflectionZoom = value;
		});
	};
}

export type AutomotivePreset = "warm day" | "cold day" | "warm night" | "cold night";

export type AutomotiveReflectionConfig = {
	image_url?: string;
	image_file?: File;
	preset: AutomotivePreset;
};
